import { useState, useRef, useCallback, useEffect } from "react";
import moment from "moment";
import { NEW_DATA_ENTRY, NEW_QA_REVIEW } from "../../../../routeNames.js";
import { isNotNumberOrNumberString } from "../../DataEntry/Grid/gridFunctions";
import { checkExcludedPayer, search837file, search837fileAll } from "../../../../redux/grid/action";
import { headerFieldNames, initialGridData } from "../constants";

// --- All your helper functions here ---
const formatDate = (date) => (date ? moment(date).format("MM/DD/YYYY") : "");

const pathname = window.location.pathname

const getClaimFieldValue = (data, jobDataClaim, key, isSameAccNoAutomation) => {
  const automationClaimData = jobDataClaim?.[key];
  return data?.[key]
    ? data?.[key]
    : (isSameAccNoAutomation && (automationClaimData || automationClaimData === 0))
    ? automationClaimData
    : "";
};

const buildServiceLine = (
  serviceline,
  currentServiceLine,
  isSameAccNoAutomation,
  isAutomation,
  sfindex,
  filterArray,
  formEob,
  claim835Index,
  getClaimFieldValue
) => {
  let value = serviceline?.billed;
  let balance = parseFloat(value ? value : 0).toFixed(2);

  return {
    ...serviceline,
    from_date: serviceline?.from_date
      ? formatDate(serviceline?.from_date)
      : (isSameAccNoAutomation && currentServiceLine?.from_date)
      ? formatDate(currentServiceLine?.from_date)
      : "",
    to_date: serviceline?.to_date
      ? formatDate(serviceline?.to_date)
      : (isSameAccNoAutomation && currentServiceLine?.to_date)
      ? formatDate(currentServiceLine?.to_date)
      : "",
    code: serviceline?.code
      ? serviceline?.code
      : isSameAccNoAutomation
      ? currentServiceLine?.code || ""
      : "",
    modifier1: serviceline?.modifier1 || "",
    modifier2: serviceline?.modifier2 || "",
    modifier3: serviceline?.modifier3 || "",
    modifier4: serviceline?.modifier4 || "",
    units: serviceline?.units
      ? serviceline?.units
      : (isSameAccNoAutomation && currentServiceLine?.units)
      ? currentServiceLine?.units
      : "",
    procedure_type: serviceline?.procedure_type || "",
    rev_code: serviceline?.rev_code || "",
    billed: serviceline?.billed
      ? serviceline?.billed
      : (isSameAccNoAutomation && currentServiceLine?.billed)
      ? currentServiceLine?.billed
      : "",
    discount:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.discount || currentServiceLine?.discount || ""
        : "",
    allowed:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.allowed || currentServiceLine?.allowed || ""
        : "",
    deduct:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.deduct || currentServiceLine?.deduct || ""
        : "",
    coins:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.coins || currentServiceLine?.coins || ""
        : "",
    copay:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.copay || currentServiceLine?.copay || ""
        : "",
    cob:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.cob || currentServiceLine?.cob || ""
        : "",
    paid:
      isSameAccNoAutomation && isAutomation
        ? serviceline?.paid || currentServiceLine?.paid || ""
        : "",
    balance,
    ...(isSameAccNoAutomation &&
      !isNotNumberOrNumberString(
        formEob.values?.claims?.[claim835Index].service_lines?.[sfindex]
          ?.init_automation_color_id
      ) && {
        init_automation_color_id:
          formEob.values?.claims?.[claim835Index].service_lines?.[sfindex]
            ?.init_automation_color_id,
      }),
    adjustments:serviceline?.adjustments || [
      {
        adjustments_id: "",
        pc: "",
        group_code: "",
        reason_code: "",
        rmk: "",
        amount: "",
        edited:true,
      },
    ],
    billing_code: serviceline?.billing_code || "",
  };
};

const buildDefaultServiceLine = () => ({
  color_index: "",
  order: "",
  service_line_id: "",
  from_date: "",
  to_date: "",
  code: "",
  procedure_type: "HC",
  modifier1: "",
  modifier2: "",
  modifier3: "",
  modifier4: "",
  rev_code: "",
  units: "",
  patres: "",
  billed: "",
  discount: "",
  allowed: "",
  deduct: "",
  coins: "",
  copay: "",
  cob: "",
  paid: "",
  balance: "",
  edited:true,
  adjustments: [
    {
      adjustment_id: "",
      pc: "",
      group_code: "",
      reason_code: "",
      rmk: "",
      amount: "",
    },
  ],
  billing_code: "",
});

const setClaimPeriod = (claim) => {
  if (claim?.service_lines?.length > 0) {
    claim.period_start = claim.service_lines[0].from_date;
    claim.period_end = claim.service_lines[0].to_date;
    claim.service_lines.forEach((service) => {
      if (service?.from_date && !isNaN(new Date(service.from_date).getDate())) {
        if (
          claim.period_start &&
          new Date(service.from_date) <= new Date(claim.period_start)
        ) {
          claim.period_start = service.from_date;
        }
      }
      if (service?.to_date && !isNaN(new Date(service.to_date).getDate())) {
        if (
          new Date(service.to_date) >= new Date(claim.period_end)
        ) {
          claim.period_end = service.to_date;
        }
      }
    });
  }
  return claim;
};

const setClaimFinancialFields = (claim) => {
  [
    "sl_discount",
    "sl_allowed",
    "sl_deduct",
    "sl_coins",
    "sl_copay",
    "sl_cob",
    "sl_patres",
    "sl_amount",
    "sl_paid",
    "sl_total",
    "claim_discount",
    "claim_allowed",
    "claim_deduct",
    "claim_coins",
    "claim_copay",
    "claim_cob",
    "claim_patres",
    "fake_sl_patres",
    "adjustment_total",
    "claim_adjustment_total",
    "claim_adjustment_total_pr",
    "claim_paid",
    "claim_total",
  ].forEach((field) => (claim[field] = parseFloat(0).toFixed(2)));
  return claim;
};

const setClaimAdjustments = (claim) => {
  claim.claim_adjustments = [
    {
      pc: "",
      group_code: "",
      reason_code: "",
      rmk: "",
      amount: "",
      claim_adjustment_id: "",
      description: "",
    },
  ];
  return claim;
};

const setClaimPatientSubscriberProvider = (
  claim,
  data,
  jobDataClaim,
  isSameAccNoAutomation,
  getClaimFieldValue
) => {
  claim.patient = true;
  claim.subscriber = true;
  claim.rendering_provider = true;
  claim.encounter = data?.encounter_id;
  claim.patient_account_number = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_account_number",
    isSameAccNoAutomation
  );
  claim.status_code = getClaimFieldValue(
    data,
    jobDataClaim,
    "status_code",
    isSameAccNoAutomation
  );
  claim.patient_first_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_first_name",
    isSameAccNoAutomation
  );
  claim.patient_middle_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_middle_name",
    isSameAccNoAutomation
  );
  claim.patient_last_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_last_name",
    isSameAccNoAutomation
  );
  claim.patient_identifier_code = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_identifier_code",
    isSameAccNoAutomation
  );
  claim.subscriber_first_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "subscriber_first_name",
    isSameAccNoAutomation
  );
  claim.subscriber_middle_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "subscriber_middle_name",
    isSameAccNoAutomation
  );
  claim.subscriber_last_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "subscriber_last_name",
    isSameAccNoAutomation
  );
  claim.subscriber_identifier_code = getClaimFieldValue(
    data,
    jobDataClaim,
    "subscriber_identifier_code",
    isSameAccNoAutomation
  );
  claim.rendering_provider_first_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "rendering_provider_first_name",
    isSameAccNoAutomation
  );
  claim.rendering_provider_last_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "rendering_provider_last_name",
    isSameAccNoAutomation
  );
  claim.rendering_provider_identifier_code = data?.rendering_provider_identifier_code
    ? data?.rendering_provider_identifier_code
    : jobDataClaim?.rendering_provider_identifier_code || "";
  return claim;
};

const setClaimForPatientPay = (
  claim,
  data,
  jobDataClaim,
  isSameAccNoAutomation,
  getClaimFieldValue
) => {
  claim.encounter_id = data?.encounter_id;
  claim.patient = true;
  claim.subscriber = true;
  claim.rendering_provider = true;
  claim.encounter = data?.encounter_id;
  claim.patient_account_number = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_account_number",
    isSameAccNoAutomation
  );
  claim.patient_first_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_first_name",
    isSameAccNoAutomation
  );
  claim.patient_last_name = getClaimFieldValue(
    data,
    jobDataClaim,
    "patient_last_name",
    isSameAccNoAutomation
  );
  claim.period_start = formatDate(data?.period_start);
  claim.period_end = formatDate(data?.period_end);
  claim.mrn = getClaimFieldValue(
    data,
    jobDataClaim,
    "mrn",
    isSameAccNoAutomation
  );
  claim.statement_number = getClaimFieldValue(
    data,
    jobDataClaim,
    "statement_number",
    isSameAccNoAutomation
  );
  claim.statement_date = formatDate(data?.statement_date);
  claim.claim_billed = data?.claim_billed
    ? parseFloat(data?.claim_billed ? data?.claim_billed : 0).toFixed(2)
    : "";
  claim.claim_balance = parseFloat(
    parseFloat(claim.claim_billed ? claim.claim_billed : 0) -
      parseFloat(claim.claim_paid ? claim.claim_paid : 0)
  ).toFixed(2);

  return claim;
};

// --- The custom hook ---
export function useF7Search837Lookup({
  formEob,
  jobData,
  handleAutomationCalculate,
  setSelectedPayerExce,
  isViewOnllyGrid,
  updateMasterValues,
  settingAdditionalInfoFormIsExcluded,
  showAlert,
  triggerEobValidation,
  isQaScreen
}) {
    const claim835Index = 0
    const isSubJob = false
    const isFirstJob = false
    const qdxTransaction = formEob?.values?.transaction_type

    // All your states
    const [error837, setError837] = useState({
        status: false,
        message: "",
        type: '',
    })  
    const [selectedServiceLines, setSelectedServiceLines] = useState([]);
  const [patientLookup, setPatientLookup] = useState(false);
  const [patientFocus, setPatientFocus] = useState(false);
  const [patientValue, setPatientValue] = useState({
    patient_account_number: "",
    patient_last_name: "",
    patient_first_name: "",
    patient_middle_name: "",
    tax_identification_number: "",
    total_charge_amount: "",
    statement_number: "",
  });
  const [patientValue1, setPatientValue1] = useState({
    patient_account_number: false,
    patient_last_name: false,
    patient_first_name: false,
    patient_middle_name: false,
    tax_identification_number: false,
    charge_amount: false,
    statement_number: false,
  });
  const [patientValue2, setPatientValue2] = useState(false);
  const [data_837, setData837] = useState(false);
  const [patientIndex, setPatientIndex] = useState(0);
  const [cl837Index, setCl837Index] = useState(0);
  const [swalPopup, setSwalPopup] = useState(false);
  const [controller, setController] = useState(null);
  const [doubleClicked, setDoubleClicked] = useState(false);
  const [blockNpiValidation, setBlockNpiValidation] = useState(false);
  const [payerPullPath, setPayerPullPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [deletedCount, setDeletedCount] = useState(null);

  // Refs
  const checkRef = useRef([]);
  const checkAllRef = useRef();

  // Handler: Clear or set searched 837 filter
  const handleCleareOrSettSearched837filter = (type) => {
    const data = formEob?.values || {};
    const singleClaim = data?.claims?.[claim835Index] || {};
    setPatientValue(
      type === "addd"
        ? {
            ...patientValue,
            patient_account_number: singleClaim?.patient_account_number
              ? singleClaim?.patient_account_number
              : "",
            patient_first_name: "",
            patient_middle_name: "",
            patient_last_name: "",
            charge_amount: "",
            tax_identification_number: "",
            statement_number: "",
          }
        : {
            patient_account_number: "",
            patient_first_name: "",
            patient_middle_name: "",
            patient_last_name: "",
            charge_amount: "",
            tax_identification_number: "",
            statement_number: "",
          }
    );
    if (type === "cleare") {
      setPatientValue1({
        patient_account_number: false,
        patient_last_name: false,
        patient_first_name: false,
        patient_middle_name: false,
        tax_identification_number: false,
        charge_amount: false,
        statement_number: false,
      });
      if (controller) {
        controller.abort(); // Abort API when component unmounts
      }
    }
  };

  // Handler: Search 837 data
  const __handleSearchData837 = async () => {
    const { is_active, ...restPatientValue } = patientValue;
    const updatedDict = Object.fromEntries(
      Object.entries(restPatientValue).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim().toUpperCase() : value,
      ])
    );
    const updatedDict1 = Object.fromEntries(
      Object.entries(patientValue1).map(([key, value]) => [
        key,
        typeof value === "string" ? value.toUpperCase() : value,
      ])
    );
    if (JSON.stringify(updatedDict) !== JSON.stringify(updatedDict1)) {
      setLoading(true);

      const newController = new AbortController();
      setController(newController); // Store controller in state
      const signal = newController.signal;

      let validFields = Object.keys(restPatientValue).filter(
        (key) =>
          !["total_charge_amount"].includes(key) &&
          restPatientValue[key].trim().length >= 3
      );
      if (validFields.length > 0) {
        let client_id = jobData?.client_id ?? "";
        const filter_type = qdxTransaction == "PATIENT_PAY_835" ? 2 : 1;
        try {
          await search837file({
            ...restPatientValue,
            client_id,
            filter_type,
            signal,
          })((response) => {
            if (response?.status && response?.status_code == 200) {
              if (!signal.aborted) {
                setLoading(false);
                setData837(response?.data?.results);
                let selectedIndex =
                  response?.data?.results[0]?.procedure_codes?.map(
                    (_, index) => index
                  ) || [];
                setCl837Index(0);
                setSelectedServiceLines(selectedIndex);
              }
            } else {
              setLoading(false);
              setData837([]);
            }
            newController.abort();
          });
        } catch (error) {
          if (error.name === "AbortError") {
            console.log("API call was aborted");
          } else {
            console.error("API error:", error);
          }
        }
      } else {
        setLoading(false);
      }
      setPatientValue1(
        Object.fromEntries(
          Object.entries(restPatientValue).map(([key, value]) => [
            key,
            value.toUpperCase(),
          ])
        )
      );
    }
  };

  // Handler: Patient hover
  const togglePatientHoverEnter = (ind) => {
    setHoveredIndex(ind);
  };

  // Handler: Set patient index
  const handlePatientIndex = (index) => {
    setCl837Index(index);
    setPatientIndex(index);

    if (checkRef && checkRef.current) {
      var selectedIndexes = [];
      checkRef.current.map((el) => {
        if (el && el?.checked) {
          selectedIndexes.push(el.value);
        }
      });

      setTimeout(() => {
        setSelectedServiceLines(selectedIndexes);
      }, 100);
    }
  };

  // Handler: Select all service lines for a patient
  const handleSelectedServiceLines = (index) => {
    var seletedIndex = [];
    data_837[index]?.procedure_codes?.map((patient, index) => {
      seletedIndex.push(index);
    });

    setSelectedServiceLines(seletedIndex);
  };

  // Handler: Check service lines
  const handleCheckServiceLines = () => {
    if (checkRef && checkRef.current) {
      var selectedIndexes = [];
      checkRef.current.map((el) => {
        if (el && el?.checked) {
          selectedIndexes.push(el.value);
        }
      });
      setSelectedServiceLines(selectedIndexes);
    }
  };

  // Handler: Check all service lines
  const handleAllCheck = async (e, isReset = false) => {
    if (e?.target?.checked || isReset) {
      checkRef?.current?.forEach((el) => {
        if (el) {
          el.checked = true;
        }
      });
      await handleCheckServiceLines();
    } else {
      checkRef?.current?.forEach((el) => {
        if (el) {
          el.checked = false;
        }
      });
      await handleCheckServiceLines();
    }
  };

  // Handler: Activate 837
  const activate837 = (ind = null) => {
    setDoubleClicked(true);
    let doubleClicked837Index = null;
    let isDoubleClickMatched = null;
    if ((ind || ind === 0) && ind !== patientIndex) {
      doubleClicked837Index = ind;
      isDoubleClickMatched = ind;
    } else {
      doubleClicked837Index = patientIndex;
    }
    const id = data_837[doubleClicked837Index]?.id;
    if (id) {
      search837fileAll(id)(async (response) => {
        if (response?.status) {
          setSelectedPayerExce(response);
          handleInsert837Data(response, isDoubleClickMatched);
        }
      });
    } else {
      setError837({
        status: true,
        message: "INVALID",
        type: "",
      });
      setDoubleClicked(false);
    }
  };
  const handleValidateHeaderAndClaim = async (claimIndex) => {
    // Combine header fields with the specific claim's path
        const fieldsToValidate = [
        ...headerFieldNames,
        `claims.${claimIndex}` // This will validate all fields inside claims[claimIndex]
        ];
        triggerEobValidation(fieldsToValidate)
    }



  //====================== Main handler==========================

 const handlePayerExcludedPayer = (payer) => {
    return new Promise((resolve, reject) => {
      new checkExcludedPayer(jobData?.job, payer?.payer_id,jobData?.facility_id)((response) => {
        if (response) {
          resolve({ success: true, response: response });
        } else {
          reject({ success: false, message: response });
        }
      });
    });
  };

  const handleInsert837Data = useCallback( async (response, isDoubleClickMatched = null, is_excluded = false) => {
     
    
    setBlockNpiValidation(true);

      let actualIndex = cl837Index;
      if (isDoubleClickMatched !== null) actualIndex = isDoubleClickMatched;

      const data = structuredClone(response?.data);
      const claims = structuredClone(formEob.values.claims);
      const new_data_837 = structuredClone(formEob.values?.data_837 || {});
      const remaining = structuredClone(formEob.values.remaining_balance);
      const oldValue = structuredClone(formEob.values);

      // Exclusion check
    let excluded =   false;
    if(formEob?.values?.transaction_type  ==='PATIENT_PAY_835'){
      is_excluded =true
    }

    if (is_excluded === true) {
      // If caller explicitly says excluded
      settingAdditionalInfoFormIsExcluded();
      setPayerPullPath(null);
      excluded = false;
    } else {
      try {
        const payload = await handlePayerExcludedPayer(data); // API call
        if (payload?.response?.is_excluded_payer) {
          setPayerPullPath({ from: "EOB_CLAIM" });

          showAlert(
            "payerExcluded",
            "Payer is Excluded",
            null,
            null,
            (ExcludedOrNot) => {
              // If user confirms, re-run with excluded=true
              handleInsert837Data(response, null, ExcludedOrNot);
            },
            () => setPayerPullPath(null) // cancel function, do nothing
          );

          setDoubleClicked(false);
          return false; // stop here, waiting for user choice
        }
      } catch (err) {
        console.error("Error checking excluded payer", err);
      }
    }

   
        if (excluded) {
            setDoubleClicked(false);
            return false;
        }

        // Service line selection
        const selectedIndexes = checkRef.current
        .filter((el) => el?.checked)
        .map((el) => el.value);

        const selectedServiceLineIds =
        isDoubleClickMatched === null
            ? data_837[actualIndex]?.procedure_codes
                ?.filter((i, ind) => selectedIndexes.includes(String(ind)))
                ?.map((i) => String(i.billing_code))
            : data_837[actualIndex]?.procedure_codes?.map((i) =>
                String(i.billing_code)
            );

        // Claim index and jobData||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
        const jobDataClaim = structuredClone(claims[0]);
        const isSameAccNoAutomation = data?.patient_account_number === jobDataClaim?.patient_account_number && jobDataClaim?.edited_data?.patient_account_number?.auto ===true  ;
        const isAutomation =
        jobData?.is_automation_completed && jobData?.is_automation_job;


        // Helper function to get claim field value
        const getClaimFieldValue = (data, jobDataClaim, key, isSameAccNoAutomation) => {
        const AutomationClaimData = jobDataClaim?.[key];
        return data?.[key]
            ? data?.[key]
            : (isSameAccNoAutomation && (AutomationClaimData || AutomationClaimData === 0))
            ? jobDataClaim?.[key]
            : "";
        };

        // If not PATIENT_PAY_835
        if (qdxTransaction !== "PATIENT_PAY_835") {
        const filteredArray = data?.service_lines?.filter((i) =>
            selectedServiceLineIds.includes(String(i.billing_code))
        );
       const sampleADJ= 
           {
        adjustments_id: "",
        pc: "",
        group_code: "",
        reason_code: "",
        rmk: "",
        amount: "",
        edited:true,
        }
        const editedDict = {
    "from_date": {
        "old_val": null,
        "error": true
    },
    "to_date": {
        "old_val": null,
        "error": true
    },
    "code": {
        "old_val": null,
        "error": true
    },
    "procedure_type": {
        "old_val": null,
        "error": true
    },
    "modifier1": {
        "old_val": null,
        "error": true
    },
    "modifier2": {
        "old_val": null,
        "error": true
    },
    "modifier3": {
        "old_val": null,
        "error": true
    },
    "modifier4": {
        "old_val": null,
        "error": true
    },
    "rev_code": {
        "old_val": null,
        "error": true
    },
    "units": {
        "old_val": null,
        "error": true
    },
    "patres": {
        "old_val": null,
        "error": true
    },
    "billed": {
        "old_val": null,
        "error": true
    },
    "discount": {
        "old_val": null,
        "error": true
    },
    "allowed": {
        "old_val": null,
        "error": true
    },
    "deduct": {
        "old_val": null,
        "error": true
    },
    "coins": {
        "old_val": null,
        "error": true
    },
    "copay": {
        "old_val": null,
        "error": true
    },
    "cob": {
        "old_val": null,
        "error": true
    },
    "paid": {
        "old_val": null,
        "error": true
    },
    "balance": {
        "old_val": null,
        "error": true
    }
        }
        const editedAdjDict = {
            "pc": {
                "old_val": null,
                "error": true
            },
            "group_code": {
                "old_val": null,
                "error": true
            },
            "reason_code": {
                "old_val": null,
                "error": true
            },
            "rmk": {
                "old_val": null,
                "error": true
            },
            "amount": {
                "old_val": null,
                "error": true
            },
            "description": {
                "old_val": null,
                "error": true
            }
        }
        const filterArray = structuredClone(filteredArray).map(item => ({
            ...item,
            edited: true,
            ...(isQaScreen && { edited_data: editedDict }),
            adjustments:[{
                 pc: "",
                group_code: "",
                reason_code: "",
                rmk: "",
                amount: "",
                adjustment_id: "",
                edited: true,
                ...(isQaScreen && { edited_data: editedAdjDict }),
            }]
        }));
        // const serviceLines = claims[claim835Index]?.auto_order?jobDataClaim?.service_lines || []:filterArray;
        const serviceLines = jobDataClaim?.service_lines || [];
        const updatedDerviceLine = []
        let sl_balance = 0;

        // Build service lines
        // if(claims[claim835Index]?.auto_order){
            if (filterArray.length > 0) {
                let combinedArray = [...filterArray];
              if(isSameAccNoAutomation &&
                isAutomation){
                   //updat the adj from the exisitng adj from current servicline  now combine arra have pulled service line and updated adj 
                  combinedArray = combinedArray.map((item, index) => ({
                    ...item,
                    adjustments: serviceLines?.[index]?.adjustments?.length
                      ? serviceLines[index].adjustments
                      : [sampleADJ] // overwrite with latest
                  }));
                }

                if (
                serviceLines.length > filterArray.length &&
                isSameAccNoAutomation &&
                isAutomation
                ) {
                 

                  // Then push the extra elements with updated adjustments
                  const extraElements = serviceLines.slice(combinedArray.length).map(sl => ({
                    ...sl,
                    adjustments: [...sl.adjustments]
                  }));

                  combinedArray.push(...extraElements);
                }
                combinedArray.forEach((serviceline, sfindex) => {
                const currentServiceLine = jobDataClaim?.service_lines?.[sfindex] || {};
                const builtServiceLine = buildServiceLine(
                    serviceline,
                    currentServiceLine,
                    isSameAccNoAutomation,
                    isAutomation,
                    sfindex,
                    filterArray,
                    formEob,
                    claim835Index,
                    getClaimFieldValue
                );
                sl_balance = (
                    parseFloat(sl_balance) + parseFloat(builtServiceLine.balance)
                ).toFixed(2);
                updatedDerviceLine.push(builtServiceLine);
                });
            } else {
                updatedDerviceLine.push(buildDefaultServiceLine());
            }
        // }
        // Set claim fields
        const isServicelinemissmatchCount =
            // claims[claim835Index]?.auto_order &&
            filterArray.length !== serviceLines.length &&
            isAutomation &&
            isSameAccNoAutomation
            ? "Serviceline Count Mismatch"
            : "";
        claims[claim835Index].isServicelineMissmatch = isServicelinemissmatchCount;
        claims[claim835Index].sl_balance = sl_balance;
        claims[claim835Index].claim_billed = sl_balance;
        claims[claim835Index].sl_billed = sl_balance;
        claims[claim835Index].claim_balance = sl_balance;
        claims[claim835Index].encounter_id = data?.encounter_id;

        setClaimPatientSubscriberProvider(
            claims[claim835Index],
            data,
            jobDataClaim,
            isSameAccNoAutomation,
            getClaimFieldValue
        );

        setClaimFinancialFields(claims[claim835Index]);
        setClaimAdjustments(claims[claim835Index]);
        claims[claim835Index].service_lines = updatedDerviceLine;
        setClaimPeriod(claims[claim835Index]);
        claims[claim835Index] = handleAutomationCalculate(
            claims[claim835Index],
            qdxTransaction
        );
        } else {
        setClaimForPatientPay(
            claims[claim835Index],
            data,
            jobDataClaim,
            isSameAccNoAutomation,
            getClaimFieldValue
        );
        }

        // Update form state based on job type and conditions
        localStorage.setItem("837_pulled", true);
        
        const is_master_job = !isSubJob && isFirstJob;
        const is_first_job = isSubJob && isFirstJob;
        const is_normal_job = !isSubJob && !isFirstJob;
        const is_child_job = isSubJob && !isFirstJob;


  //=======================this fun will pdate only ehe edi while pull updateEditedData ==============

              const updateEditedData = (updates, baseEditedData,ediUpdatedClaims) => {
                // updates = { key1: value1, key2: value2, ... }
                return Object.keys(updates).reduce((acc, key) => {
                
                
                  let oldvalue = {};
                  let isInsertOldValue = false
                  if (   pathname === NEW_QA_REVIEW && (
                      ediUpdatedClaims?.edited_data?.[`patient_account_number`]?.edi  !== formEob?.values?.claims?.[0]?.[`patient_account_number`]   //if user pull the different payer payee then mark the qa as error
                      //|| ediUpdatedClaims?.[key] !== formEob?.values?.[key]
                      )
                     ) {


                    oldvalue = {
                      ...(!('old_val' in (formEob?.values?.edited_data?.[key] || {})) && { old_val: null }) , //{ old_val: formEob?.values?.[key] || "" }
                      error: true
                    }
                    isInsertOldValue = true

                  }



                  return {
                    ...acc,


                    [key]: {
                      ...(acc?.[key] || {}),
                      ...(updates[key] && {edi: updates[key] }),
                       ...(isInsertOldValue && { ...oldvalue })

                    }
                  };
                }, { ...baseEditedData });
              };


        function updateClaimEdi(claim,replaceClaim, fieldKeys) {
          const updatedEditedData = { ...(claim.edited_data || {}) };

          fieldKeys.forEach((key) => {

            

             let value = replaceClaim[key]

              if (['period_start','period_end'].includes(key)) {
                if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                  // Convert "YYYY-MM-DD" → "MM/DD/YYYY"
                  const [year, month, day] = value.split("-");
                  value = `${month}/${day}/${year}`;
                } else if (!value) {
                  // If empty or invalid, force null
                  value = '';
                }
              }

            let oldvalue = {};
            let isInsertOldValue = false
            if (
              pathname === NEW_QA_REVIEW 
              // &&
              // (
              //   claim?.edited_data?.[`patient_account_number`]?.edi !== formEob?.values?.claims?.[0]?.[`patient_account_number`] ||  //if user pull the different payer payee then mark the qa as error
              //   updatedEditedData?.[key] !== formEob?.values?.claims?.[0]?.[key]
              // )

            ) {


              oldvalue = {
                ...(!('old_val' in (formEob?.values?.claims?.[0]?.edited_data?.[key] || {})) && { old_val:  null }),  //{ old_val: formEob?.values?.claims?.[0]?.[key] || null }
                error: true
              }
              isInsertOldValue = true

            }

              

            // if (value !== null && value !== undefined && value !== "") {

              updatedEditedData[key] = {
                ...(value && updatedEditedData[key] || {}), // keep old_value if exists
                ...(value && { edi: value }), // take current claim value
                ...(isInsertOldValue && { ...oldvalue })

              // };

              // if (!("old_val" in updatedEditedData[key]) && value) {
              //   updatedEditedData[key].old_val = "";
              // }
            }

          });
          if(window.location.pathname===NEW_QA_REVIEW)updatedEditedData.pull_error = true
          
          return[ {
            ...claim,
            edited:true,
            edited_data: updatedEditedData,
          }];
        }

        // function updateServiceLinesAndAdjustmentsEdi(claim, serviceLineCount, serviceLineFieldKeys, adjustmentFieldKeys) {
        //   const updatedClaim = { ...claim };
        //   const updatedServiceLines = [...(claim.service_lines || [])];

        //   // Loop through servicelines up to the given count
        //   for (let i = 0; i < serviceLineCount && i < updatedServiceLines.length; i++) {
        //     const line = updatedServiceLines[i];
        //     const updatedEditedData = { ...(line.edited_data || {}) };

        //     // ✅ Update serviceline edited_data
        //     serviceLineFieldKeys.forEach((key) => {
        //       updatedEditedData[key] = {
        //         ...(updatedEditedData[key] || {}),
        //         ...(line[key] && { edi: line[key] }), //newClaim[0].service_lines[key] &&  { edi: newClaim[0].service_lines[key] }
        //       };
        //     });

        //     // ✅ Update adjustments inside this serviceline
        //     const updatedAdjustments = (line.adjustments || []).map((adj) => {
        //       const updatedAdjEditedData = { ...(adj.edited_data || {}) };

        //       adjustmentFieldKeys.forEach((key) => {
        //         updatedAdjEditedData[key] = {
        //           ...(updatedAdjEditedData[key] || {}),
        //           ...(adj[key] && { edi: adj[key] }),
        //         };
        //       });

        //       return {
        //         ...adj,
        //         edited_data: updatedAdjEditedData,
        //       };
        //     });

        //     // Replace updated line
        //     updatedServiceLines[i] = {
        //       ...line,
        //       edited_data: updatedEditedData,
        //       adjustments: updatedAdjustments,
        //     };
        //   }

        //   return [{
        //     ...updatedClaim,
        //     service_lines: updatedServiceLines,
        //   }];
        // }
        function updateServiceLinesAndAdjustmentsEdi(
          claim,   //this is form Eob Claim wth updated  edited value only form  claim section neow need to update SL and ADJ
          newClaim,                 // ✅ new claim passed here
          serviceLineCount,
          serviceLineFieldKeys,
          adjustmentFieldKeys
        ) {
          const updatedClaim = { ...claim };
          const updatedServiceLines = [...(claim.service_lines || [])];
          const newServiceLines = newClaim?.service_lines || [];

          // Loop through servicelines up to the given count
          for (let i = 0; i < serviceLineCount && i < updatedServiceLines.length; i++) {
            const line = updatedServiceLines[i];
            const newLine = newServiceLines[i] || {};
            const updatedEditedData = { ...(line.edited_data || {}) };

           

            //  Update serviceline edited_data using newClaim values
            serviceLineFieldKeys.forEach((key) => {

               //date conversion '12-18-2024' to "12/18/2024"
              let value = newLine[key];

              if (["from_date",'to_date'].includes(key)) {
                if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                  // Convert "YYYY-MM-DD" → "MM/DD/YYYY"
                  const [year, month, day] = value.split("-");
                  value = `${month}/${day}/${year}`;
                } else if (!value) {
                  // If empty or invalid, force null
                  value = '';
                }
              }
              if (value !== null && value !== undefined && value !== "") {

                let oldvalue = {};
                let isInsertOldValue = false
                if (pathname === NEW_QA_REVIEW &&
                  (
                    claim?.edited_data?.[`patient_account_number`]?.edi !== formEob?.values?.claims?.[0]?.[`patient_account_number`] ||   //if user pull the different payer payee then mark the qa as error or pull first time 
                    updatedEditedData?.[key] !== claim?.[key]

                  )) {

                  oldvalue = {
                    ...(!('old_val' in (formEob?.values?.claims?.[0]?.service_lines?.[i]?.edited_data?.[key] || {})) && {
                      old_val:null, // formEob?.values?.claims?.[0]?.service_lines?.[i]?.[key] ||""
                    }),
                    error: true
                  };
                  isInsertOldValue = true

                }

              


                updatedEditedData[key] = {
                  ...(updatedEditedData[key] || {}),
                  ...(value && { edi: value }),
                  ...(isInsertOldValue && { ...oldvalue })
                }
                // if (!("old_val" in updatedEditedData[key]) && value) {
                //   updatedEditedData[key].old_val = "";
                // }
              }
            });

            //  Update adjustments inside this serviceline
            const updatedAdjustments = (line.adjustments || []).map((adj, adjIndex) => {
              const newAdj = (newLine.adjustments || [])[adjIndex] || {};
              const updatedAdjEditedData = { ...(adj.edited_data || {}) };

              adjustmentFieldKeys.forEach((key) => {

                const formeobCurrentAdj = formEob?.values?.claims?.[0]?.service_lines?.[i]?.adjustments[adjIndex]

                let oldvalue = {};
                let isInsertOldValue = false
                if (pathname === NEW_QA_REVIEW &&
                  (
                    (claim?.edited_data?.[`patient_account_number`]?.edi !== claim?.[`patient_account_number`])   // 1. if user pull the different payer payee then mark the qa as error or pull first time 
                    //  || updatedEditedData?.[key] !== formeobCurrentAdj?.[key]
                  ) // 2. if user pul the sam payer payee then the data is missmatch the qa marked as the error
                ) {

                  oldvalue = {
                    ...(!formeobCurrentAdj?.edited_data?.[key]?.old_val && { old_val: formeobCurrentAdj?.[key] || "" }),
                    error: true
                  }
                  isInsertOldValue = true

                }

                

                const value = newAdj[key]
                if (value !== null && value !== undefined && value !== "") {
                  updatedAdjEditedData[key] = {
                    ...(updatedAdjEditedData[key] || {}),
                    ...(value && { edi: value }),
                     ...(isInsertOldValue && { ...oldvalue })
                  }
                  // if (!("old_val" in updatedEditedData[key])) {
                  //   updatedEditedData[key].old_val = "";
                  // }
                }

              });

              return {
                ...adj,
                edited:true,
                edited_data: updatedAdjEditedData,
              };
            });

            // Replace updated line
            updatedServiceLines[i] = {
              ...line,
              edited:true,
              edited_data: updatedEditedData,
              adjustments: updatedAdjustments,
            };
          }

          return [
            {
              ...updatedClaim,
              service_lines: updatedServiceLines,
            },
          ];
        }


        const claimEdited_data_Key = [
          'patient_account_number',
          'payer_claim_number',
          'period_start',
          'period_end',
          'payer_claim_number',
          'status_code',
          'mrn',
          'statement_number',
          'page_from',
          'page_to',

          'patient_first_name',
          'patient_middle_name',
          'patient_last_name',
          'patient_identifier_code',
          'prior_auth_code',

          'subscriber_first_name',
          'subscriber_middle_name',
          'subscriber_last_name',
          'subscriber_identifier_code',

          'rendering_provider_first_name',
          'rendering_provider_last_name',
          'rendering_provider_identifier_code',


          ...(qdxTransaction === "PATIENT_PAY_835"
            ? ['claim_billed', 'claim_paid', 'statement_date']
            : []),
          ...(qdxTransaction === "PAYMENT835" ?
            ["claim_allowed",
              "claim_balance",
              "claim_billed",
              "claim_cob",
              "claim_coins",
              "claim_copay",
              "claim_deduct",
              "claim_discount",
              "claim_paid",
              "claim_patres"] : []
          ),
        ]
        const service_line_edited_data_key = [
          'allowed',
          'amount',
          'balance',
          'billed',
          'billing_code',
          'cob',
          'code',
          'coins',
          'deduct',
          'discount',
          'from_date',
          'modifier1',
          'modifier2',
          'modifier3',
          'modifier4',
          'paid',
          'procedure_type',
          'rev_code',
          'to_date',
          'units',
]

        const updatedClaimWitEDI = updateClaimEdi(claims[0], data,claimEdited_data_Key);
        const updatedClaimSlAdjEdi = updateServiceLinesAndAdjustmentsEdi(
          updatedClaimWitEDI?.[0],
          data,
          (data?.service_lines?.length ||0) ,
          service_line_edited_data_key, // serviceline fields
          [ "amount","pc" ,"group_code", "reason_code", "rmk"] // adjustment fields
        );


        if (
        (new_data_837?.ediPull === false || new_data_837?.ediPull === undefined) || //no 837 pull till now
        window.location.pathname == NEW_QA_REVIEW ||
        new_data_837?.automation837pull === true // auto 837 pulled
        ) {
           
            if (is_master_job || is_first_job || is_normal_job) {
                new_data_837.ediPull = true

             
              

              const updates = {

                payee_name: data?.payee_name || "",
                payee_npi: data?.payee_npi || "",
                payee_address: data?.payee_address || "",
                payee_city: data?.payee_city || "",
                payee_state: data?.payee_state || "",
                payee_zip: data?.payee_zip || "",
                payer_pulled_id: data?.payer_id || "",
                payer_name: data?.payer_name || "",
                payer_address: data?.payer_address || "",
                payer_city: data?.payer_city || "",
                payer_state: data?.payer_state || "",
                payer_zip: data?.payer_zip || "",
                reference_entity_identifier: data?.reference_entity_identifier || "",
                tax_identification_number: data?.tax_identification_number || "",
                
              };
              const onlySaveUpdate = {
                payee_id: data?.payee_id || "",
                payee__pulled_id: data?.payee_id || "",
                payer_id: data?.payer_id || "",
             }

            const updatedEditedDataHeader = updateEditedData(updates, formEob.values?.edited_data ,updatedClaimWitEDI?.[0]);

                // Set comprehensive form values for master/first/normal job
                const newValues = {
                ...formEob.values,
                remaining_balance: parseFloat(
                    parseFloat(remaining || 0) +
                    parseFloat(oldValue?.claims[claim835Index]?.claim_paid || 0) -
                    parseFloat(claims?.[claim835Index]?.claim_paid || 0)
                ).toFixed(2),
               ...updates,
               ...onlySaveUpdate,
                data_837: new_data_837,
                claims: updatedClaimSlAdjEdi,
                qdx_status: (formEob?.values?.qdx_status === 'INCOMPLETE' && 
                            formEob?.values?.qdx_reason?.some(item => item?.label === 'Excluded Payer')) 
                            ? "" : formEob?.values?.qdx_status,
                qdx_reason: (formEob?.values?.qdx_status === 'INCOMPLETE' && 
                            formEob?.values?.qdx_reason?.some(item => item?.label === 'Excluded Payer'))
                            ? formEob?.values?.qdx_reason.filter(item => item?.label != 'Excluded Payer')
                            : formEob?.values?.qdx_reason,
               edited_data: updatedEditedDataHeader,

                };
                updateMasterValues(newValues);
                handleValidateHeaderAndClaim(claim835Index)

            } else if (is_child_job) {
                const newValues = {
                ...formEob.values,
                remaining_balance: parseFloat(
                    parseFloat(remaining || 0) +
                    parseFloat(oldValue?.claims[claim835Index]?.claim_paid || 0) -
                    parseFloat(claims?.[claim835Index]?.claim_paid || 0)
                ).toFixed(2),
                data_837: new_data_837,
                claims: claims,
                };
                updateMasterValues(newValues);
                handleValidateHeaderAndClaim(claim835Index)
            }
        } else {
            const newValues = {
                ...formEob.values,
                remaining_balance: parseFloat(
                parseFloat(remaining || 0) +
                parseFloat(oldValue?.claims[claim835Index]?.claim_paid || 0) -
                parseFloat(claims?.[claim835Index]?.claim_paid || 0)
                ).toFixed(2),
                data_837: new_data_837,
                claims: updatedClaimSlAdjEdi,
                qdx_status: (formEob?.values?.qdx_status === 'INCOMPLETE' && 
                            formEob?.values?.qdx_reason?.some(item => item?.label === 'Excluded Payer')) 
                            ? "" : formEob?.values?.qdx_status,
                qdx_reason: (formEob?.values?.qdx_status === 'INCOMPLETE' && 
                            formEob?.values?.qdx_reason?.some(item => item?.label === 'Excluded Payer'))
                            ? formEob?.values?.qdx_reason.filter(item => item?.label != 'Excluded Payer')
                            : formEob?.values?.qdx_reason,
            };
            updateMasterValues(newValues);
            handleValidateHeaderAndClaim(claim835Index)
        }

        setPatientValue1({
        patient_account_number: false,
        patient_first_name: false,
        patient_middle_name: false,
        patient_last_name: false,
        charge_amount: false,
        tax_identification_number: false,
        statement_number: false,
        });

        setSelectedServiceLines([]);
        setPatientLookup(false);
        setPatientFocus(true);
        setDoubleClicked(false);

        setTimeout(() => {
        if (pathname === NEW_DATA_ENTRY && claim835Index === 0) {
            const claimAccountNo = document.querySelector(`[name="payer_name"]`);
            if (claimAccountNo) claimAccountNo.focus();
        }
        }, 50);
    },
    [
        cl837Index,
        claim835Index,
        formEob,
        jobData,
        qdxTransaction,
        handleAutomationCalculate,
        isNotNumberOrNumberString,
        NEW_DATA_ENTRY,
        pathname,
        payerPullPath,
        handlePayerExcludedPayer,
        deletedCount,
        setDeletedCount,
        isSubJob,
        isFirstJob,
        NEW_QA_REVIEW,
    ]
    );

  // Handler: Payer Excluded Payer
 

  // Handler: Keyup for F7
  const handleAccountNumberKeyUp = useCallback(
    (event) => {
      if (
        document.activeElement?.getAttribute('name')=== `claims.${0}.patient_account_number` &&
        event.keyCode === 118 && // F7
        !isViewOnllyGrid
      ) {
        
        setPatientLookup(true);
        setPatientIndex(0);
        setPatientValue(prevState => ({...prevState,patient_account_number: event.target.value}))
      }
    },
    [isViewOnllyGrid]
  );
  // Handler: Close F7 Component
  const handleClose837Search = useCallback(
    (event) => {
        if(patientLookup){
            setPatientLookup(false)
            setPatientValue1({
                patient_account_number: false,
                patient_last_name: false,
                patient_first_name: false,
                patient_middle_name: false,
                tax_identification_number: false,
                charge_amount: false,
                statement_number: false,
            })
            setTimeout(()=>{
                document.querySelector(`[name="claims.${claim835Index}.patient_account_number"]`)?.focus();
            },50)
          setPatientValue({
            patient_account_number: "",
            patient_last_name: "",
            patient_first_name: "",
            patient_middle_name: "",
            tax_identification_number: "",
            total_charge_amount: "",
            statement_number: "",
          })
        }
    },
    [patientLookup,setPatientLookup]
  );

    useEffect(() => {
    if (!patientLookup) return; // Only add listener if patientLookup is true

    const handleKeyUp = (event) => {
        if (event.key === "Escape" || event.keyCode === 27) {
        handleClose837Search(event);
        }else if (event.keyCode === 38) {
            if (patientIndex > 0) {
                handleAllCheck(null,true)
                setPatientIndex(patientIndex - 1);
                setCl837Index(patientIndex - 1);
                handleSelectedServiceLines(patientIndex - 1)
            }
        }else if (event.keyCode === 40) {
            if (patientIndex < data_837?.length - 1) {
                handleAllCheck(null,true)
                setPatientIndex(patientIndex + 1);
                setCl837Index(patientIndex + 1);
                handleSelectedServiceLines(patientIndex + 1)
            }
        }else if (patientLookup && event.keyCode === 13 && event.ctrlKey == true) {
            activate837();
        }
    };

    window.addEventListener("keyup", handleKeyUp);

    return () => {
        window.removeEventListener("keyup", handleKeyUp);
    };
    }, [patientLookup, handleClose837Search,patientIndex,handleAllCheck,setPatientIndex,setCl837Index,handleSelectedServiceLines,activate837]);

  return {
    selectedServiceLines,
    setSelectedServiceLines,
    patientLookup,
    setPatientLookup,
    patientFocus,
    setPatientFocus,
    patientValue,
    setPatientValue,
    patientValue1,
    setPatientValue1,
    patientValue2,
    setPatientValue2,
    data_837,
    setData837,
    patientIndex,
    setPatientIndex,
    cl837Index,
    setCl837Index,
    swalPopup,
    setSwalPopup,
    controller,
    setController,
    doubleClicked,
    setDoubleClicked,
    blockNpiValidation,
    setBlockNpiValidation,
    payerPullPath,
    setPayerPullPath,
    loading,
    setLoading,
    hoveredIndex,
    setHoveredIndex,
    checkRef,
    checkAllRef,
    handleCleareOrSettSearched837filter,
    __handleSearchData837,
    togglePatientHoverEnter,
    handlePatientIndex,
    handleSelectedServiceLines,
    handleCheckServiceLines,
    handleAllCheck,
    activate837,
    handleInsert837Data,
    handlePayerExcludedPayer,
    handleAccountNumberKeyUp,
    error837,
    handleClose837Search
  };
}